import { useMemo } from 'react';
import { Card, Text } from '@tremor/react';
import { formatMxn } from '../lib/format';
import { t } from '../lib/i18n';
import type { SalesSummary } from '../api/types';
import ErrorRetry from './ErrorRetry';
import ToggleableChart, { type Slice } from './ToggleableChart';

interface Props {
  summary: SalesSummary | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /**
   * Σ balance_mxn > 0 from the raw ticket detail, once it has arrived. The
   * summary itself has no paid/pending split (that needs the per-ticket artrx
   * lookup it exists to avoid), so this KPI fills in behind the others.
   */
  pendingMxn: number | null;
  /** Wired once the raw detail is available — the line drill needs the rows. */
  onLineClick?: (line: string) => void;
}

function typeLabel(type: number): string {
  switch (type) {
    case 0: return t.hourDrill.typeCredit;
    case 1: return t.hourDrill.typeCreditDebit;
    case 2: return t.hourDrill.typeCash;
    case 3: return t.hourDrill.typeCashDebit;
    default: return String(type);
  }
}

/**
 * KPI strip + the three breakdown charts for a sales window, fed by the
 * server-side aggregate (/api/sales-summary) instead of the raw ticket list.
 * Same visual vocabulary as SalesAggregationView (the modal drill-downs), but
 * the buckets arrive pre-summed from the daemon so a 30-day window paints in
 * seconds while /api/sales is still scanning.
 */
export default function SalesSummaryPanel({ summary, loading, error, onRetry, pendingMxn, onLineClick }: Props) {
  const byType: Slice[] = useMemo(
    () => (summary?.by_type ?? [])
      .map((b) => ({ label: typeLabel(b.type), value: b.value }))
      .sort((a, b) => b.value - a.value),
    [summary],
  );
  const byLine: Slice[] = useMemo(
    () => (summary?.by_line ?? [])
      .filter((b) => b.value !== 0)
      .map((b) => ({ label: b.line.trim() !== '' ? b.line : '—', value: b.value })),
    [summary],
  );
  const bySlid: Slice[] = useMemo(
    () => (summary?.by_slid ?? [])
      .map((b) => ({ label: b.slid.trim() !== '' ? b.slid : t.hourDrill.noSlid, value: b.value })),
    [summary],
  );

  const s = t.sales.summary;
  const pendingWarn = pendingMxn !== null && pendingMxn > 0;

  return (
    <div className="space-y-4" data-testid="sales-summary">
      {error && <ErrorRetry message={error} onRetry={onRetry} />}
      {!error && loading && !summary && (
        <Card>
          <Text className="text-sm text-tremor-content animate-pulse">{s.loading}</Text>
        </Card>
      )}
      {summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Kpi label={s.tickets} value={summary.totals.tickets.toLocaleString('es-MX')} />
            <Kpi label={s.sales}   value={formatMxn(summary.totals.sales_mxn)} />
            <Kpi label={s.cash}    value={formatMxn(summary.totals.cash_mxn)} />
            <Kpi label={s.credit}  value={formatMxn(summary.totals.credit_mxn)} />
            <Kpi
              label={s.pending}
              value={pendingMxn === null ? '…' : formatMxn(pendingMxn)}
              sub={pendingMxn === null ? s.pendingWaiting : undefined}
              warn={pendingWarn}
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ToggleableChart title={t.hourDrill.byType}        data={byType} initial="pie" />
            <ToggleableChart title={t.hourDrill.byLine}        data={byLine} initial="bar" onSliceClick={onLineClick} />
            <ToggleableChart title={t.hourDrill.bySalesperson} data={bySlid} initial="bar" />
          </div>
          <Text className="text-xs text-tremor-content">{s.hint}</Text>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <Card className="p-4">
      <Text className="text-xs text-tremor-content">{label}</Text>
      <div
        className={
          'text-xl font-semibold tabular-nums ' +
          (warn ? 'text-red-600 dark:text-red-400' : 'text-tremor-content-strong')
        }
      >
        {value}
      </div>
      {sub && <Text className="text-xs text-tremor-content">{sub}</Text>}
    </Card>
  );
}
